/**
 * Helpers pour le calcul des lignes service auto-ajoutées à une commande
 * lorsque l'option "avec service" est cochée.
 *
 * Doc fonctionnelle : lemoulinapi/docs/pro/prestation-service.md
 */

/**
 * Calcule la quantité (en heures) facturée par ligne de service :
 *   durée événement + 2h MEP + 1h rangement
 *
 * @param {string|null} startAt - "HH:mm" ou "HH:mm:ss" (ex: "10:00:00")
 * @param {string|null} endAt   - idem
 * @returns {number} heures (arrondi 2 décimales). Fallback 3h si horaires absents.
 */
export const calculateServiceQuantity = (startAt, endAt) => {
  if (!startAt || !endAt) return 3;

  const parseTime = (s) => {
    const [h, m] = String(s).split(":").map(Number);
    return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
  };

  let diffMin = parseTime(endAt) - parseTime(startAt);
  if (diffMin < 0) diffMin += 24 * 60; // event qui passe minuit

  const hours = diffMin / 60 + 3;
  return Math.round(hours * 100) / 100;
};

/**
 * Nombre de serveurs : +1 par tranche de 50 convives, le premier palier de 50
 * étant couvert par le maître d'hôtel seul. 1–50 → 0, 51–100 → 1, 101–150 → 2…
 *
 * @param {number|string|null} person - nombre de convives
 * @returns {number}
 */
export const calculateWaiterCount = (person) => {
  const n = Number(person);
  if (!Number.isFinite(n) || n <= 0) return 0;
  // Premier palier de 50 → 0 serveur (le maître d'hôtel suffit). Puis +1 / 50.
  return Math.max(0, Math.ceil(n / 50) - 1);
};

/**
 * Construit l'item à dispatcher via addOrderItem(orderIndex, item).
 * Format aligné avec ProductsComponent.handleAddProduct.
 *
 * @param {object} serviceProduct - élément du state.prestationService.list
 * @param {number} quantity
 */
export const buildServiceLineItem = (serviceProduct, quantity) => ({
  name: serviceProduct.name,
  quantity,
  price: serviceProduct.price,
  product_id: serviceProduct.id,
  classification_name: serviceProduct.categoryName || null,
  type: "product",
});

/**
 * Seuil à partir duquel les heures sont considérées "de nuit" (tarif majoré
 * via les produits taggés `service-apres-23h` côté backend).
 */
export const NIGHT_THRESHOLD_HOUR = 23;

/**
 * Split de la durée serveur en heures jour vs nuit, en tenant compte de :
 *  - 2h de mise en place avant `startAt`
 *  - 1h de rangement après `endAt`
 *  - le seuil de nuit (par défaut 23h00) ; tout ce qui est >= 23h tombe en nuit.
 *
 * Exemple : presta 20:00 → 00:00 → serveur 18:00 → 01:00
 *           → 5h jour (18→23) + 2h nuit (23→01) = 7h total.
 *
 * @param {string|null} startAt - "HH:mm" ou "HH:mm:ss" (heure début presta)
 * @param {string|null} endAt   - idem (heure fin presta)
 * @returns {{total:number, day:number, night:number}} heures (arrondies 2 déc).
 *          Fallback `{total: 3, day: 3, night: 0}` si horaires absents.
 */
export const calculateServiceHoursSplit = (startAt, endAt) => {
  if (!startAt || !endAt) {
    return { total: 3, day: 3, night: 0 };
  }

  const parseTime = (s) => {
    const [h, m] = String(s).split(":").map(Number);
    return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
  };

  // Plage serveur en minutes (depuis 00:00 du jour de la presta).
  // -2h amont (peut être négatif si la presta démarre tôt → on borne à 0).
  let startServiceMin = parseTime(startAt) - 2 * 60;
  if (startServiceMin < 0) startServiceMin = 0;

  // +1h aval. Si endAt < startAt côté brut → event qui passe minuit, on ajoute 24h.
  let endServiceMin = parseTime(endAt) + 1 * 60;
  if (endServiceMin <= startServiceMin) endServiceMin += 24 * 60;

  const thresholdMin = NIGHT_THRESHOLD_HOUR * 60;
  const dayMin   = Math.max(0, Math.min(thresholdMin, endServiceMin) - startServiceMin);
  const nightMin = Math.max(0, endServiceMin - Math.max(thresholdMin, startServiceMin));

  const round2 = (x) => Math.round(x * 100) / 100;
  return {
    total: round2((endServiceMin - startServiceMin) / 60),
    day:   round2(dayMin / 60),
    night: round2(nightMin / 60),
  };
};
